<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDialogPluginComponent } from 'quasar';

import type {
  ConsultationDto,
  FormDto,
  Locales,
  VisibilityCondition,
  VisibilityRuleSet,
} from '@/services/types/api.types';
import { useConsultationsStore, useFormsStore, useSectionsStore } from '@/stores';
import { Constants } from '@/constants';
import { getTranslation } from '@/composables/data-display-utils';
import { formTitle } from '@/helpers/visibilityRules.helper';
import { wrapSavingOperation } from '@/helpers/notifications.helper';

interface LocalCondition {
  id: string;
  sourceFormId: number | null;
  valueId: number | null;
}

const { t } = useI18n();
const consultationsStore = useConsultationsStore();
const formsStore = useFormsStore();
const sectionsStore = useSectionsStore();

const props = defineProps<{
  formId: number;
  consultation: ConsultationDto;
  languageEnabled: Locales[];
}>();

defineEmits([...useDialogPluginComponent.emits]);
const { dialogRef, onDialogHide, onDialogOK, onDialogCancel } = useDialogPluginComponent();

const existingRuleSet = computed(
  () =>
    (props.consultation.options?.guided_consultation?.visibility?.ruleSets ?? []).find(
      (rs) => rs.targetId === props.formId,
    ) ?? null,
);

const visibilityEnabled = ref(existingRuleSet.value !== null);
const localConditions = ref<LocalCondition[]>(
  existingRuleSet.value
    ? existingRuleSet.value.conditions.map((c) => ({
        id: c.id,
        sourceFormId: c.sourceFormId,
        valueId: (c.valueIds?.[0] as number) ?? null,
      }))
    : [],
);
const consultationForms = ref<FormDto[]>([]);
const isLoadingForms = ref(false);
const formsInitialized = ref(false);

const availableSourceForms = computed(() => {
  const currentIndex = consultationForms.value.findIndex((f) => f.id === props.formId);
  const before = currentIndex >= 0 ? consultationForms.value.slice(0, currentIndex) : [];
  return before.filter(
    (f) => f.type === Constants.Form.Type.Question && (f.answers?.steps?.[0]?.choices?.length ?? 0) > 0,
  );
});

const sourceFormSelectOptions = computed(() =>
  availableSourceForms.value.map((f) => ({
    value: f.id,
    label: formTitle(availableSourceForms.value, f.id, props.languageEnabled),
  })),
);

function getChoiceSelectOptions(sourceFormId: number | null) {
  if (!sourceFormId) return [];
  const f = consultationForms.value.find((form) => form.id === sourceFormId);
  return (f?.answers?.steps?.[0]?.choices ?? []).map((c) => ({
    value: c.id as number,
    label: getTranslation(c, 'text', props.languageEnabled) || String(c.id),
  }));
}

async function loadConsultationForms() {
  if (isLoadingForms.value || formsInitialized.value) return;
  isLoadingForms.value = true;
  try {
    await sectionsStore.fetchSectionsByConsultation(props.consultation.id!);
    const sections = sectionsStore.getSectionsByConsultationId(props.consultation.id!);
    const sortedSections = [...sections].sort((a, b) => a.order_num - b.order_num);
    for (const s of sortedSections) {
      if (s.id) await formsStore.fetchFormsBySection(s.id);
    }
    const allForms: FormDto[] = [];
    for (const s of sortedSections) {
      if (!s.id) continue;
      const sectionForms = formsStore.getFormsBySectionId(s.id);
      allForms.push(...[...sectionForms].sort((a, b) => a.order_num - b.order_num));
    }
    consultationForms.value = allForms;
  } finally {
    isLoadingForms.value = false;
    formsInitialized.value = true;
  }
}

function addCondition() {
  localConditions.value.push({ id: crypto.randomUUID(), sourceFormId: null, valueId: null });
}

function removeCondition(index: number) {
  localConditions.value.splice(index, 1);
}

function onVisibilityToggle(enabled: boolean) {
  if (enabled && localConditions.value.length === 0) addCondition();
}

function buildUpdatedVisibilityRuleSets(): VisibilityRuleSet[] {
  const existingRuleSets = (props.consultation.options?.guided_consultation?.visibility?.ruleSets ?? []).filter(
    (rs) => rs.targetId !== props.formId,
  );
  if (!visibilityEnabled.value) return existingRuleSets;
  const conditions: VisibilityCondition[] = localConditions.value
    .filter((c) => c.sourceFormId !== null && c.valueId !== null)
    .map((c) => ({
      id: c.id,
      sourceFormId: c.sourceFormId!,
      operator: 'equals' as const,
      valueIds: [c.valueId!],
    }));
  if (conditions.length === 0) return existingRuleSets;
  return [
    ...existingRuleSets,
    {
      id: `rule_target_${props.formId}`,
      targetType: 'form' as const,
      targetId: props.formId,
      match: 'all' as const,
      conditions,
    },
  ];
}

const onSave = async () => {
  const updatedConsultation: ConsultationDto = {
    ...props.consultation,
    options: {
      ...props.consultation.options,
      guided_consultation: {
        resultsMode:
          props.consultation.options?.guided_consultation?.resultsMode ??
          Constants.Consultation.GuidedConsultation.ResultsMode.CompletedOnly,
        visibility: { ruleSets: buildUpdatedVisibilityRuleSets() },
      },
    },
  };
  await wrapSavingOperation(
    async () => consultationsStore.save(updatedConsultation),
    () => onDialogOK(),
  );
};

loadConsultationForms();
</script>

<template>
  <q-dialog ref="dialogRef" @hide="onDialogHide" persistent>
    <q-card class="q-dialog-plugin" style="width: 600px; max-width: 90vw">
      <q-card-section class="row items-center">
        <div class="text-h6">{{ t('form.guided-consultation.visibility.title') }}</div>
        <q-space />
        <q-btn flat round icon="close" size="sm" @click="onDialogCancel" />
      </q-card-section>
      <q-separator />
      <q-card-section>
        <q-toggle
          v-model="visibilityEnabled"
          :label="t('form.guided-consultation.visibility.toggleLabel')"
          @update:model-value="onVisibilityToggle"
        />
        <template v-if="visibilityEnabled">
          <div v-if="isLoadingForms" class="q-mt-sm q-ml-lg">
            <q-spinner-dots color="primary" size="sm" />
          </div>
          <template v-else-if="formsInitialized">
            <q-banner v-if="availableSourceForms.length === 0" class="bg-amber-1 q-mt-sm text-caption" rounded>
              {{ t('form.guided-consultation.visibility.noSourceForms') }}
            </q-banner>
            <div v-else class="q-mt-sm q-ml-lg">
              <div class="text-caption text-grey-7 q-mb-sm">
                {{ t('form.guided-consultation.visibility.header') }}
              </div>
              <div
                v-for="(condition, index) in localConditions"
                :key="condition.id"
                class="row items-center q-gutter-sm q-mb-sm"
              >
                <q-select
                  v-model="condition.sourceFormId"
                  :options="sourceFormSelectOptions"
                  option-value="value"
                  option-label="label"
                  emit-value
                  map-options
                  outlined
                  dense
                  :label="t('form.guided-consultation.visibility.sourceQuestion')"
                  @update:model-value="condition.valueId = null"
                  class="col"
                />
                <span class="text-body2 text-grey-8">{{ t('form.guided-consultation.visibility.operator') }}</span>
                <q-select
                  v-model="condition.valueId"
                  :options="getChoiceSelectOptions(condition.sourceFormId)"
                  option-value="value"
                  option-label="label"
                  emit-value
                  map-options
                  outlined
                  dense
                  :label="t('form.guided-consultation.visibility.answer')"
                  :disable="!condition.sourceFormId"
                  class="col"
                />
                <q-btn flat round icon="close" size="sm" color="grey-6" @click="removeCondition(index)" />
              </div>
              <div v-if="localConditions.length > 1" class="text-caption text-grey-7 q-mb-xs">
                {{ t('form.guided-consultation.visibility.allConditionsTrue') }}
              </div>
              <q-btn
                flat
                icon="add"
                :label="t('form.guided-consultation.visibility.addCondition')"
                color="primary"
                size="sm"
                no-caps
                @click="addCondition"
              />
            </div>
          </template>
        </template>
      </q-card-section>
      <q-separator />
      <q-card-actions align="right">
        <q-btn color="primary" flat :label="t('global.cancel')" @click="onDialogCancel" />
        <q-btn color="primary" :label="t('global.save')" @click="onSave" />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>
